import React, { useEffect, useMemo, useRef, useState } from 'react';

import { searchLocumsAsAdmin, searchStoresAsAdmin } from '../Api';
import SearchCombobox from './SearchCombobox';
import { getRecentStoreIds, indexStores, searchStores, toStoreSummary } from './adminShiftUtils';

const MIN_LOCUM_QUERY_LENGTH = 2;
const MIN_SERVER_STORE_QUERY_LENGTH = 2;
const LOCUM_SEARCH_DEBOUNCE_MS = 250;
// Stores picked from the server search this visit, kept outside the component so the Recent chips survive the
// picker remounting when moving between the edit and review steps.
const sessionStores = new Map();

export const getLocumName = (locum) => `${locum.firstName || ''} ${locum.lastName || ''}`.trim();

/** Searches locums on the server as you type (debounced, cached, older requests cancelled). */
export const LocumPicker = ({ locum, onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const cache = useRef(new Map());

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_LOCUM_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      setError('');
      return undefined;
    }
    const cacheKey = trimmed.toLowerCase();
    if (cache.current.has(cacheKey)) {
      setResults(cache.current.get(cacheKey));
      setLoading(false);
      setError('');
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { locums } = await searchLocumsAsAdmin(trimmed, controller.signal);
        cache.current.set(cacheKey, locums);
        setResults(locums);
        setError('');
        setLoading(false);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError('Search failed, try again.');
          setLoading(false);
        }
      }
    }, LOCUM_SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  return (
    <SearchCombobox
      id={'admin-locum-search'}
      label={'Locum'}
      placeholder={'Search by name, email or phone'}
      selectedText={locum ? getLocumName(locum) : ''}
      query={query}
      onQueryChange={setQuery}
      results={results}
      loading={loading}
      error={error}
      minLength={MIN_LOCUM_QUERY_LENGTH}
      emptyText={'No locums found'}
      getResultKey={(item) => item._id}
      onSelect={onSelect}
      renderResult={(item) => (
        <div className={'picker-result'}>
          <span className={'picker-result-title'}>{getLocumName(item)}</span>
          <span className={'picker-result-detail'}>{[item.type, item.emailAddress, item.phoneNumber].filter(Boolean).join(' · ')}</span>
          <span className={'picker-result-detail'}>{`AHPRA ${item.ahpra || 'missing'} · ABN ${item.abn || 'missing'}`}</span>
        </div>
      )}
    />
  );
};

/**
 * Searches every store. The admin account's linked stores are searched locally (instant), and from 2 characters all
 * stores are searched on the server (debounced, cached). Stores not yet linked are marked and get linked when booked.
 */
export const StorePicker = ({ stores, store, onSelect }) => {
  const [query, setQuery] = useState('');
  const [serverResults, setServerResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const cache = useRef(new Map());
  const indexedStores = useMemo(() => indexStores(stores), [stores]);
  const localResults = useMemo(() => searchStores(indexedStores, query).map(toStoreSummary), [indexedStores, query]);
  const results = useMemo(() => {
    const localIds = new Set(localResults.map(item => item._id));
    return [...localResults, ...serverResults.filter(item => !localIds.has(item._id))];
  }, [localResults, serverResults]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_SERVER_STORE_QUERY_LENGTH) {
      setServerResults([]);
      setLoading(false);
      setError('');
      return undefined;
    }
    const cacheKey = trimmed.toLowerCase();
    if (cache.current.has(cacheKey)) {
      setServerResults(cache.current.get(cacheKey));
      setLoading(false);
      setError('');
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { stores: found } = await searchStoresAsAdmin(trimmed, controller.signal);
        cache.current.set(cacheKey, found);
        setServerResults(found);
        setError('');
        setLoading(false);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError('Searching all stores failed. Only the stores already linked to this account are shown.');
          setLoading(false);
        }
      }
    }, LOCUM_SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const handleSelect = (item) => {
    sessionStores.set(item._id, item);
    onSelect(item);
  };

  const recentStores = useMemo(() => {
    const byId = new Map((stores || []).map(item => [item._id, item]));
    return getRecentStoreIds()
      .map(id => (byId.has(id) ? toStoreSummary(byId.get(id)) : sessionStores.get(id)))
      .filter(Boolean);
  // Recompute when a different store is picked so the chips reorder.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores, store]);

  return (
    <div className={'store-picker'}>
      <SearchCombobox
        id={'admin-store-search'}
        label={'Store (any store, linked or not)'}
        placeholder={'Search all stores by name, suburb, postcode or business'}
        selectedText={store ? store.name : ''}
        query={query}
        onQueryChange={setQuery}
        results={results}
        loading={loading}
        error={error && results.length === 0 ? error : ''}
        emptyText={'No stores match'}
        getResultKey={(item) => item._id}
        onSelect={handleSelect}
        renderResult={(item) => (
          <div className={'picker-result'}>
            <span className={'picker-result-title'}>
              {item.name}
              { item.linked === false && <span className={'picker-badge'}>{'Not linked yet'}</span> }
            </span>
            <span className={'picker-result-detail'}>{[item.address, item.registeredBusinessName].filter(Boolean).join(' · ')}</span>
          </div>
        )}
      />
      { recentStores.length > 0 &&
        <div className={'recent-stores'}>
          <span className={'recent-stores-label'}>{'Recent:'}</span>
          { recentStores.map(item => (
            <button
              key={item._id}
              type={'button'}
              className={`recent-store-chip${store && store._id === item._id ? ' active' : ''}`}
              onClick={() => handleSelect(item)}
            >
              {item.name}
            </button>
          ))}
        </div>
      }
    </div>
  );
};
